import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { EntryKind, TagKind } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useId, useRef, useState } from 'react'
import { ArrowRight, BookOpen, GraduationCap, LoaderCircle, MessageCircleQuestion, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TAG_KINDS, entryKindMeta, tagHref } from '@/lib/kinds'

interface Suggestions {
    tags: { id: string, slug: string, kind: TagKind, name: string, name_hi: string | null, entry_count: number }[]
    entries: { id: string, slug: string, kind: EntryKind, title: string }[]
    qa: { id: string, slug: string, question: string }[]
    sessions: { id: string, slug: string, title: string, course_title: string | null, course_id: string | null }[]
}

interface SuggestionItem {
    key: string
    href: string
    node: ReactNode
}

interface SearchBoxProps {
    size?: 'lg' | 'md'
    autoFocus?: boolean
    defaultValue?: string
    placeholder?: string
    onNavigate?: () => void
    className?: string
}

const EMPTY: Suggestions = { tags: [], entries: [], qa: [], sessions: [] }

const toItems = (results: Suggestions): { title: string, items: SuggestionItem[] }[] => {
    const groups = [
        {
            title: 'Samagri, events, places and topics',
            items: results.tags.map(tag => ({
                key: `tag-${tag.id}`,
                href: tagHref(tag.kind, tag.slug),
                node: (
                    <span className='flex w-full items-center gap-2'>
                        <span className={cn('size-2.5 shrink-0 rounded-full border border-foreground', TAG_KINDS[tag.kind]?.tone)} aria-hidden='true' />
                        <span className='truncate font-semibold' data-mdlfy-cms={`tags:${tag.id}:name`}>{tag.name}</span>
                        <span className='ml-auto shrink-0 font-mono text-[10px] uppercase text-muted-foreground'>{TAG_KINDS[tag.kind]?.singular} · {tag.entry_count}</span>
                    </span>
                )
            }))
        },
        {
            title: 'Remedies, methods and more',
            items: results.entries.map(entry => ({
                key: `entry-${entry.id}`,
                href: `/entries/${entry.slug}`,
                node: (
                    <span className='flex w-full items-center gap-2'>
                        <BookOpen className='size-4 shrink-0' aria-hidden='true' />
                        <span className='truncate' data-mdlfy-cms={`entries:${entry.id}:title`}>{entry.title}</span>
                        <span className='ml-auto shrink-0 font-mono text-[10px] uppercase text-muted-foreground'>{entryKindMeta(entry.kind).label}</span>
                    </span>
                )
            }))
        },
        {
            title: 'Questions and answers',
            items: results.qa.map(item => ({
                key: `qa-${item.id}`,
                href: `/qa/${item.slug}`,
                node: (
                    <span className='flex w-full items-center gap-2'>
                        <MessageCircleQuestion className='size-4 shrink-0' aria-hidden='true' />
                        <span className='truncate' data-mdlfy-cms={`questions:${item.id}:question`}>{item.question}</span>
                    </span>
                )
            }))
        },
        {
            title: 'Sessions',
            items: results.sessions.map(session => ({
                key: `session-${session.id}`,
                href: `/sessions/${session.slug}`,
                node: (
                    <span className='flex w-full items-center gap-2'>
                        <GraduationCap className='size-4 shrink-0' aria-hidden='true' />
                        <span className='truncate' data-mdlfy-cms={`sessions:${session.id}:title`}>{session.title}</span>
                        {session.course_title && session.course_id && (
                            <span className='ml-auto shrink-0 truncate font-mono text-[10px] uppercase text-muted-foreground' data-mdlfy-cms={`courses:${session.course_id}:title`}>{session.course_title}</span>
                        )}
                    </span>
                )
            }))
        }
    ]

    return groups.filter(group => group.items.length > 0)
}

const SearchBox: FC<SearchBoxProps> = ({ size = 'md', autoFocus = false, defaultValue = '', placeholder = 'Search peeli sarson, griha pravesh, north-east...', onNavigate, className }): ReactNode => {
    const router = useRouter()
    const inputId = useId()
    const listId = useId()
    const containerRef = useRef<HTMLDivElement>(null)
    const [value, setValue] = useState(defaultValue)
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [failed, setFailed] = useState(false)
    const [results, setResults] = useState<Suggestions>(EMPTY)
    const [active, setActive] = useState(-1)

    useEffect(() => {
        setValue(defaultValue)
    }, [defaultValue])

    useEffect(() => {
        const term = value.trim()

        if (term.length < 2) {
            setResults(EMPTY)
            setLoading(false)
            setFailed(false)

            return
        }

        const controller = new AbortController()
        const timer = window.setTimeout(async () => {
            setLoading(true)
            setFailed(false)

            try {
                const response = await fetch(`/api/search?q=${encodeURIComponent(term)}`, { signal: controller.signal })

                if (!response.ok) throw new Error('Search failed')

                const data = await response.json() as Suggestions

                setResults({
                    tags: data.tags ?? [],
                    entries: data.entries ?? [],
                    qa: data.qa ?? [],
                    sessions: data.sessions ?? []
                })
                setActive(-1)
            }
            catch (error) {
                if ((error as Error).name !== 'AbortError') setFailed(true)
            }
            finally {
                if (!controller.signal.aborted) setLoading(false)
            }
        }, 220)

        return () => {
            controller.abort()
            window.clearTimeout(timer)
        }
    }, [value])

    useEffect(() => {
        const close = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false)
        }

        document.addEventListener('mousedown', close)

        return () => document.removeEventListener('mousedown', close)
    }, [])

    const groups = toItems(results)
    const flat = groups.flatMap(group => group.items)
    const showPanel = open && value.trim().length >= 2

    const go = (href: string) => {
        setOpen(false)
        onNavigate?.()
        router.push(href)
    }

    const submit = () => {
        const term = value.trim()

        if (!term) return

        go(`/search?q=${encodeURIComponent(term)}`)
    }

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setOpen(true)
            setActive(current => (flat.length ? (current + 1) % flat.length : -1))
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActive(current => (flat.length ? (current <= 0 ? flat.length - 1 : current - 1) : -1))
        }
        else if (event.key === 'Escape') {
            setOpen(false)
        }
        else if (event.key === 'Enter' && active >= 0 && flat[active]) {
            event.preventDefault()
            go(flat[active].href)
        }
    }

    const large = size === 'lg'
    let index = -1

    return (
        <div ref={containerRef} className={cn('relative w-full', className)}>
            <form
                role='search'
                onSubmit={event => {
                    event.preventDefault()
                    submit()
                }}
                className='flex w-full gap-2'
            >
                <div className='relative min-w-0 flex-1'>
                    <label htmlFor={inputId} className='sr-only'>
                        Search the knowledge base
                    </label>

                    <Search className={cn('pointer-events-none absolute left-4 top-1/2 -translate-y-1/2', large ? 'size-5' : 'size-4')} aria-hidden='true' />

                    <input
                        id={inputId}
                        type='search'
                        value={value}
                        autoFocus={autoFocus}
                        autoComplete='off'
                        placeholder={placeholder}
                        role='combobox'
                        aria-expanded={showPanel}
                        aria-controls={listId}
                        aria-autocomplete='list'
                        onChange={event => {
                            setValue(event.target.value)
                            setOpen(true)
                        }}
                        onFocus={() => setOpen(true)}
                        onKeyDown={onKeyDown}
                        className={cn(
                            'w-full rounded-xl border-2 border-foreground bg-card font-medium shadow-brutal outline-none placeholder:text-muted-foreground/80 focus-visible:outline-none focus-visible:ring-0',
                            large ? 'h-14 pl-12 pr-4 text-base md:text-lg' : 'h-11 pl-10 pr-3 text-sm'
                        )}
                    />

                    {loading && (
                        <LoaderCircle className='absolute right-4 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground' aria-hidden='true' />
                    )}
                </div>

                <button
                    type='submit'
                    className={cn(
                        'inline-flex shrink-0 items-center gap-2 rounded-xl border-2 border-foreground bg-primary font-bold shadow-brutal transition-transform hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-brutal-sm',
                        large ? 'h-14 px-5 text-base' : 'h-11 px-4 text-sm'
                    )}
                >
                    <span className={cn(!large && 'sr-only sm:not-sr-only')}>Search</span>
                    <ArrowRight className={large ? 'size-5' : 'size-4'} aria-hidden='true' />
                </button>
            </form>

            {showPanel && (
                <div id={listId} role='listbox' className='absolute left-0 right-0 top-full z-50 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border-2 border-foreground bg-card p-2 text-left shadow-brutal-lg'>
                    {failed && (
                        <p className='px-3 py-4 text-sm text-muted-foreground'>
                            Search is not responding right now. Press Enter to try the full search page.
                        </p>
                    )}

                    {!failed && !loading && groups.length === 0 && (
                        <p className='px-3 py-4 text-sm text-muted-foreground'>
                            No quick matches. Press Enter to search everything.
                        </p>
                    )}

                    {groups.map(group => (
                        <div key={group.title} className='py-1'>
                            <p className='px-3 pb-1 pt-2 font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground'>
                                {group.title}
                            </p>

                            {group.items.map(item => {
                                index += 1
                                const current = index

                                return (
                                    <Link
                                        key={item.key}
                                        href={item.href}
                                        role='option'
                                        aria-selected={active === current}
                                        onMouseEnter={() => setActive(current)}
                                        onClick={() => {
                                            setOpen(false)
                                            onNavigate?.()
                                        }}
                                        className={cn('flex rounded-lg px-3 py-2 text-sm', active === current ? 'bg-primary' : 'hover:bg-secondary')}
                                    >
                                        {item.node}
                                    </Link>
                                )
                            })}
                        </div>
                    ))}

                    <button
                        type='button'
                        onClick={submit}
                        className='mt-1 flex w-full items-center justify-between rounded-lg border-t-2 border-dashed border-foreground/20 px-3 py-2.5 text-sm font-bold hover:bg-secondary'
                    >
                        See all results
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </button>
                </div>
            )}
        </div>
    )
}

export default SearchBox
